import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';

import MapLocations from '@/components/branches/MapLocations';
import BranchInfo from '@/components/branches/BranchInfo';
import BreadCrumb from '@/components/global/BreadCrumb';
import { getBranches } from '@/services/product';
import dynamic from 'next/dynamic';
import LazyLoading from '@/components/LazyLoading';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { event_global_variables } from '@/utils/gtm';
import { reconcileBranchSelection } from '@/utils/branches/selection';
const LeafletMap = dynamic(
  () => import('../../components/branches/LeafletMap'),
  {
    loading: () => <LazyLoading parentClassName={'min-h-[400px]'} />,
    ssr: false,
  }
);

const Index = ({ data: ApiData = [] }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('common');
  const [branchInView, setBranchInView] = useState(ApiData?.at(0));

  const [data, setData] = useState([]);

  // A language switch runs getServerSideProps again but keeps this page
  // mounted, so the branch picked under the previous locale is read again
  // from the new records (same id, else the first one, else none) and the
  // list, the selector label and the map move together.
  useEffect(() => {
    setData(ApiData);
    setBranchInView((current) => reconcileBranchSelection(current, ApiData));
  }, [ApiData]);

  const { asPath } = useRouter();
  const { data: session } = useSession();
  useEffect(() => {
    const prepareData = () => {
      event_global_variables(
        language,
        session?.user?.id,
        session?.user?.email,
        session?.user?.custom_attributes
          ?.find((item) => item?.attribute_code == 'mobilenumber')
          ?.value?.slice(2),
        session?.user?.city && session?.user?.city
      );
    };
    prepareData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asPath, session?.user]);

  return (
    <section className='container mx-auto max-md:px-5'>
      <section className='my-4 sm:my-8'>
        <div className='mb-4 flex flex-wrap justify-between gap-4 max-sm:flex-col sm:mb-8 sm:items-center'>
          <h1 className='text-[13px] font-bold sm:text-3xl sm:font-semibold'>
            {t('Our Branches')}
          </h1>
          <div className='flex items-center gap-2'>
            <MapLocations
              t={t}
              ApiData={ApiData}
              branchInView={branchInView}
              setBranchInView={setBranchInView}
            />
          </div>
        </div>
        <div className='grid grid-cols-12 sm:gap-4'>
          <div className='red-scrollbar gray-scroll order-2 col-span-full max-h-[80vh] overflow-y-auto *:mb-3 max-lg:gap-4 max-lg:overflow-x-auto lg:order-1 lg:col-span-4'>
            {data.map((info, i) => (
              <BranchInfo
                key={`branches-page-${i}`}
                t={t}
                info={info}
                branchInView={branchInView}
                setBranchInView={setBranchInView}
              />
            ))}
          </div>
          <div className='order-1 col-span-full lg:order-2 lg:col-span-8'>
            <LeafletMap
              data={data}
              branchInView={branchInView}
              setBranchInView={setBranchInView}
            />
          </div>
        </div>
      </section>
    </section>
  );
};

Index.getUnderHeaderBar = (_, { t }) => (
  <BreadCrumb current={t('Our Branches', { ns: 'common' })} />
);

export default Index;

export async function getServerSideProps({ locale }) {
  // QA-296: the store locator is a public locale GET, so no handshake is
  // minted for it
  const { data, error } = await getBranches(locale);
  if (error) {
    return {
      props: {},
    };
  }

  return {
    props: {
      data: data?.data || [],
      ...(await serverSideTranslations(locale, ['common'])),
    },
  };
}
